import type { components } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, EmptyState, Icon } from "@sagot/ui";
import { selectedPageCount } from "./crawl-model";

const KB_ADD = "deployKnowledge.knowledge.add";
const K = "onboarding.knowledge";

type Discovery = components["schemas"]["DiscoveryResult"];

interface CrawlResultsProps {
  result: Discovery;
  /** Listed pages the user switched off (they become exclude paths when the crawl is created). */
  deselected: ReadonlySet<string>;
  adding: boolean;
  onToggle: (url: string, selected: boolean) => void;
  onSelectAll: () => void;
  onClear: () => void;
  onAdd: () => void;
}

function pathOf(url: string): string {
  try {
    const { pathname, search } = new URL(url);
    return `${pathname}${search}`;
  } catch {
    return url;
  }
}

/** Discovered pages: robots / sitemap facts, a selectable list, and the button that starts the crawl. */
export function CrawlResults({ result, deselected, adding, onToggle, onSelectAll, onClear, onAdd }: CrawlResultsProps) {
  const { t } = useTranslation();
  const selected = selectedPageCount(result.page_count, deselected.size);
  const blocked = !result.allowed_by_robots;
  if (result.page_count === 0) {
    return (
      <div className="rounded-lg border border-line" data-testid="crawl-results">
        <EmptyState icon="globe" headingLevel={3} title={t(`${K}.crawlNothingTitle`)} description={t(`${K}.crawlNothingBody`)} />
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-line" data-testid="crawl-results">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-surface-2 px-4 py-2.5">
        <span className="text-xs font-bold text-fg" aria-live="polite">
          {result.capped ? t(`${K}.crawlFoundCapped`, { count: result.page_count }) : t(`${K}.crawlFound`, { count: result.page_count })} · {t(`${K}.crawlSelected`, { count: selected })}
        </span>
        <div className="flex gap-3">
          <Button variant="ghost" size="sm" onClick={onSelectAll} disabled={deselected.size === 0}>
            {t(`${K}.crawlSelectAll`)}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClear} disabled={deselected.size >= result.sample.length - 1}>
            {t(`${K}.crawlClear`)}
          </Button>
        </div>
      </div>
      <ul className="max-h-72 divide-y divide-line overflow-y-auto" aria-label={t(`${K}.crawlListLabel`)}>
        {result.sample.map((url) => {
          const isStart = url === result.start_url;
          return (
            <li key={url} className="px-4 py-2">
              <Checkbox
                checked={!deselected.has(url)}
                disabled={isStart}
                onCheckedChange={(checked) => { onToggle(url, checked === true); }}
                label={<span className="break-all font-mono text-xs">{pathOf(url)}</span>}
                description={isStart ? t(`${K}.crawlStartPage`) : undefined}
              />
            </li>
          );
        })}
      </ul>
      <div className="space-y-2 border-t border-line px-4 py-3">
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-muted">
          <li className="flex items-center gap-1">
            <Icon name={blocked ? "alert-triangle" : "check-circle"} className={blocked ? "size-3.5 text-warning" : "size-3.5 text-success"} />
            {blocked ? t(`${KB_ADD}.robotsBlocked`) : t(`${KB_ADD}.robotsAllowed`)}
          </li>
          {result.sitemap_found ? (
            <li className="flex items-center gap-1">
              <Icon name="check-circle" className="size-3.5 text-success" />
              {t(`${KB_ADD}.sitemapFound`)}
            </li>
          ) : null}
          {result.files > 0 ? <li>{t(`${KB_ADD}.previewFiles`, { count: result.files })}</li> : null}
        </ul>
        {result.page_count > result.sample.length ? <p className="text-xs text-fg-subtle">{t(`${K}.crawlShowing`, { shown: result.sample.length, total: result.page_count })}</p> : null}
        <div className="flex justify-end">
          <Button size="sm" loading={adding} disabled={selected === 0 || blocked} onClick={onAdd}>
            <Icon name="plus" />
            {t(`${K}.crawlAdd`, { count: selected })}
          </Button>
        </div>
      </div>
    </div>
  );
}
